import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { FileText, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { Avatar } from '@/components/ui/avatar'
import { Pill, type PillTone } from '@/components/ui/pill'
import { usePageHeader } from '@/components/page-header-context'

type KycStatus = 'PENDING' | 'KYC_SUBMITTED' | 'APPROVED' | 'REJECTED' | 'SUSPENDED'

interface DriverKycItem {
  driverId: string
  firstName: string
  lastName: string
  phone: string | null
  kycStatus: KycStatus
  documentCount: number
  createdAt: string
}

interface DriverKycListResponse {
  data: DriverKycItem[]
  total: number
}

const TABS: { key: KycStatus | ''; label: string }[] = [
  { key: 'KYC_SUBMITTED', label: 'À traiter' },
  { key: 'APPROVED', label: 'Approuvés' },
  { key: 'REJECTED', label: 'Refusés' },
  { key: '', label: 'Tous' },
]

function useAllKycDrivers() {
  return useQuery<DriverKycListResponse>({
    queryKey: ['kyc-drivers', 'all'],
    queryFn: async () => {
      const { data } = await api.get('/admin/drivers?take=100')
      return data
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  })
}

function statusPill(d: DriverKycItem): { label: string; tone: PillTone } {
  if (d.kycStatus === 'KYC_SUBMITTED' && d.documentCount < 6)
    return { label: 'INCOMPLET', tone: 'danger' }
  switch (d.kycStatus) {
    case 'APPROVED':
      return { label: 'KYC_APPROVED', tone: 'success' }
    case 'REJECTED':
      return { label: 'KYC_REJECTED', tone: 'danger' }
    case 'KYC_SUBMITTED':
      return { label: 'KYC_SUBMITTED', tone: 'neutral' }
    case 'SUSPENDED':
      return { label: 'SUSPENDED', tone: 'danger' }
    default:
      return { label: 'PENDING_KYC', tone: 'neutral' }
  }
}

export default function DriverKycList() {
  const [filter, setFilter] = useState<KycStatus | ''>('KYC_SUBMITTED')
  const { data, isLoading, isError } = useAllKycDrivers()
  const all = data?.data ?? []

  const submitted = all.filter((d) => d.kycStatus === 'KYC_SUBMITTED').length
  usePageHeader(
    'Vérification KYC',
    `${submitted} dossier${submitted > 1 ? 's' : ''} chauffeur${submitted > 1 ? 's' : ''} en attente de validation`,
  )

  const counts: Record<string, number> = {
    KYC_SUBMITTED: submitted,
    APPROVED: all.filter((d) => d.kycStatus === 'APPROVED').length,
    REJECTED: all.filter((d) => d.kycStatus === 'REJECTED').length,
    '': all.length,
  }

  const rows = filter ? all.filter((d) => d.kycStatus === filter) : all

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* tabs */}
      <div className="flex gap-2">
        {TABS.map((t) => {
          const active = filter === t.key
          return (
            <button
              key={t.key || 'all'}
              onClick={() => setFilter(t.key)}
              className={`flex items-center gap-2 h-9 px-4 rounded-pill border text-[13px] font-semibold transition-colors ${
                active
                  ? 'bg-ink border-ink text-surface'
                  : 'bg-surface border-line text-ink-soft hover:bg-surfaceAlt'
              }`}
            >
              {t.label}
              <span
                className={`font-mono text-[11px] font-bold ${active ? 'text-primary' : 'text-muted'}`}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          )
        })}
      </div>

      {/* table */}
      <div className="mt-[18px] bg-surface rounded-[18px] border border-line overflow-hidden flex-1 flex flex-col min-h-0">
        <div className="grid grid-cols-[2.4fr_1.6fr_1fr_1.3fr_1fr] gap-4 px-6 py-3.5 border-b border-line bg-surfaceAlt">
          {['Chauffeur', 'Soumis le', 'Documents', 'Statut', ''].map((h, i) => (
            <span
              key={h || 'action'}
              className={`text-[11px] font-bold tracking-wide uppercase text-muted ${i === 4 ? 'text-right' : ''}`}
            >
              {h}
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && <p className="p-6 text-muted">Chargement…</p>}
          {isError && (
            <p className="p-6 text-danger">Erreur lors du chargement des dossiers KYC.</p>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <p className="p-8 text-center text-muted">Aucun dossier.</p>
          )}
          {!isLoading &&
            !isError &&
            rows.map((d, i) => {
              const name = `${d.firstName} ${d.lastName}`.trim() || '—'
              const complete = d.documentCount >= 6
              const pill = statusPill(d)
              return (
                <div
                  key={d.driverId}
                  className={`grid grid-cols-[2.4fr_1.6fr_1fr_1.3fr_1fr] gap-4 items-center px-6 py-3.5 border-b border-line last:border-0 ${
                    i === 0 ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={name} size={40} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{name}</div>
                      <div className="font-mono text-[11.5px] text-muted mt-0.5">
                        {d.phone ?? '—'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-ink-soft">
                      {new Date(d.createdAt).toLocaleDateString('fr-TN')}
                    </div>
                    <div className="text-[11.5px] text-muted mt-px">
                      {formatDistanceToNow(new Date(d.createdAt), {
                        addSuffix: true,
                        locale: fr,
                      })}
                    </div>
                  </div>
                  <div>
                    <span
                      className={`inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-pill ${
                        complete ? 'bg-success/15' : 'bg-primary/15'
                      }`}
                    >
                      <FileText
                        size={13}
                        strokeWidth={2.2}
                        className={complete ? 'text-success' : 'text-muted'}
                      />
                      <span
                        className={`font-mono text-[11.5px] font-bold ${complete ? 'text-success' : 'text-ink-soft'}`}
                      >
                        {d.documentCount}/6
                      </span>
                    </span>
                  </div>
                  <div>
                    <Pill tone={pill.tone} mono className="!px-2.5 !py-1 text-[11px]">
                      {pill.label}
                    </Pill>
                  </div>
                  <div className="flex justify-end">
                    <Link
                      to="/kyc/$driverId"
                      params={{ driverId: d.driverId }}
                      className={`h-[34px] px-4 rounded-[10px] flex items-center gap-1.5 text-[12.5px] font-semibold ${
                        i === 0 ? 'bg-ink text-surface' : 'bg-surfaceAlt text-ink hover:bg-line'
                      }`}
                    >
                      Examiner
                      <ChevronRight size={14} strokeWidth={2.4} />
                    </Link>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
